import * as THREE from 'three';
import { emis, std, stone } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import type { Rng } from '../rand.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Prisma (the Chasm's Geode guardian, docs/SIMA.md «Guardianes»): «Los cristales lo cargan: rompelos.» A golem
 * of the geode: its body a great split stone whose open chest is a cave of violet prisms round a white-hot core;
 * clusters of crystal erupt from its shoulders like pauldrons, its arms are long six-sided prisms ending in fists
 * of rock and crystal, its small head is crowned with prisms and lit by two slits of the core's light. The core
 * is where the beam comes out.
 *
 * How the integrator drives it (anim/creatures/prism.ts): 'beam' (2.4 s): it charges (the `core` bone swells,
 * 0–0.7 s), fires from its chest (the `beam` bone, built tiny, full from 0.75 s) and sweeps it across before it,
 * the beam gone by 2.0 s. The crystals that charge it are the `crystal` prop (models/sima-props.ts).
 *
 * Bones: body (hips), chest, head, `core`, `beam`, arms (arm, fore, hand), legs (leg, shin). Faces +Z.
 */

/** How small the beam is built (the beam clip scales `beam` by 1 / PRISM_BEAM_REST). */
export const PRISM_BEAM_REST = 0.02;
export const PRISM_HEIGHT = 2.75;

/** A six-sided prism with a pointed tip, from its base at the origin up +Y (`len` the side, `tip` the point). */
export const hexPrism = (r: number, len: number, tip: number): THREE.BufferGeometry =>
  new THREE.LatheGeometry([new THREE.Vector2(0.001, 0), new THREE.Vector2(r, 0), new THREE.Vector2(r, len), new THREE.Vector2(0.001, len + tip)], 6);

/** A prism standing from `at` along `dir`. */
export const prismAt = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, dir: THREE.Vector3, r: number, len: number, rng: Rng): THREE.Mesh => {
  const m = M(hexPrism(r, len, r * 1.6), mat, parent);
  m.position.copy(at);
  m.quaternion.setFromUnitVectors(V(0, 1, 0), dir.clone().normalize());
  m.rotateY(rng() * 6.3);
  return m;
};

export const prism = (o?: BaseOpts & { firing?: boolean }): Model => {
  const c = begin(o, 1.15, 6161);
  const { g, rng } = c;
  const k4 = kit(rng);

  const rock = stone('#26232c', { bumpScale: 0.05 });
  const rockL = stone('#423d4c', { bumpScale: 0.05 });
  const crystal = emis('#dcb6ff', '#9b3fe0', 0.7, { transparent: true, opacity: 0.86, roughness: 0.1, flatShading: true });
  const deep = std('#5a2d7e', { roughness: 0.18, metalness: 0.3, flatShading: true });
  const core = emis('#fbefff', '#c56cff', 2.6);
  const beamM = emis('#d49bff', '#c56cff', 2.4, { transparent: true, opacity: 0.7, depthWrite: false });

  const P = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, dir: THREE.Vector3, r: number, len: number): THREE.Mesh => prismAt(parent, mat, at, dir, r, len, rng);
  /** A cluster of prisms from `at`, fanned round `dir`. */
  const cluster = (parent: THREE.Object3D, at: THREE.Vector3, dir: THREE.Vector3, n: number, len: number, r: number): void => {
    for (let i = 0; i < n; i++) {
      const d = dir.clone().add(V((rng() - 0.5) * 0.9, (rng() - 0.5) * 0.4, (rng() - 0.5) * 0.9));
      P(parent, i % 3 === 2 ? deep : crystal, at.clone().add(V((rng() - 0.5) * 0.1, (rng() - 0.5) * 0.06, (rng() - 0.5) * 0.1)), d, r * (0.7 + rng() * 0.5), len * (0.55 + rng() * 0.6) * (i === 0 ? 1.3 : 1));
    }
  };

  /* ---------- legs: pillars of split rock, crystal breaking out of them ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = joint(g, V(k * 0.36, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    k4.lump(hip, rock, k * 0.38, 0.8, 0.02, 0.24, 0.3, 0.24, 0.3, 1);
    cluster(hip, V(k * 0.55, 0.85, -0.02), V(k, 0.4, -0.2), 3, 0.3, 0.06);
    const shinB = joint(hip, V(k * 0.4, 0.55, 0.03), `shin${s}`);
    const lo = shinB.abs;
    k4.lump(lo, rockL, k * 0.4, 0.34, 0.05, 0.22, 0.26, 0.22, 0.3, 1);
    k4.lump(lo, rock, k * 0.42, 0.1, 0.12, 0.28, 0.12, 0.34, 0.2, 1);
    P(lo, crystal, V(k * 0.5, 0.35, 0.18), V(k * 0.5, 0.5, 1), 0.06, 0.2);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips ---------- */
  const bodyB = joint(g, V(0, 1.05, 0), 'body');
  const body = bodyB.abs;
  k4.lump(body, rock, 0, 1.08, -0.02, 0.48, 0.24, 0.36, 0.2, 1);

  /* ---------- chest: the split geode, a cave of prisms round the core ---------- */
  const chestB = joint(body, V(0, 1.25, 0), 'chest');
  const chest = chestB.abs;
  k4.lump(chest, rock, 0, 1.7, -0.32, 0.76, 0.62, 0.36, 0.2, 1, 0.12);
  for (const k of [-1, 1]) k4.lump(chest, rock, k * 0.5, 1.62, -0.05, 0.3, 0.5, 0.36, 0.2, 1);
  k4.lump(chest, rockL, 0, 1.3, 0.0, 0.5, 0.24, 0.38, 0.2, 1);
  const C = V(0, 1.68, 0.2);
  // The back of the cave, dark.
  k4.lump(chest, deep, C.x, C.y, C.z - 0.24, 0.36, 0.3, 0.08, 0.2, 0);
  // The lips of the split stone round the opening.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.2;
    k4.lump(chest, i % 2 ? rock : rockL, C.x + Math.cos(a) * 0.44, C.y + Math.sin(a) * 0.38, C.z, 0.15, 0.14, 0.16, 0.4, 0);
  }
  // The druse: prisms pointing out from the back of the cave toward the opening.
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    const rr = 0.12 + (i % 3) * 0.08;
    const at = V(C.x + Math.cos(a) * rr, C.y + Math.sin(a) * rr * 0.85, C.z - 0.2);
    P(chest, i % 4 === 0 ? deep : crystal, at, V(Math.cos(a) * 0.7, Math.sin(a) * 0.6, 1), 0.035 + rng() * 0.02, 0.14 + rng() * 0.12);
  }
  // Seams of the core's light cracking out over the stone.
  for (const [a, l] of [[0.5, 0.5], [2.4, 0.45], [4.1, 0.4], [5.4, 0.42]] as const) {
    const p0 = V(C.x + Math.cos(a) * 0.5, C.y + Math.sin(a) * 0.42, C.z - 0.02);
    k4.crack(chest, [p0, p0.clone().add(V(Math.cos(a) * l * 0.5, Math.sin(a) * l * 0.5, -0.08)), p0.clone().add(V(Math.cos(a + 0.2) * l, Math.sin(a + 0.2) * l, -0.2))], 0.013, core, 0.05);
  }
  // Shoulders: great clusters of crystal erupting up and out.
  for (const k of [-1, 1]) {
    k4.lump(chest, rock, k * 0.62, 2.0, -0.08, 0.3, 0.26, 0.3, 0.3, 1);
    cluster(chest, V(k * 0.68, 2.12, -0.1), V(k * 0.55, 1, -0.25), 5, 0.75, 0.09);
  }
  // Prisms down the spine.
  for (let i = 0; i < 4; i++) P(chest, i % 2 ? deep : crystal, V((i % 2 ? 0.12 : -0.12), 2.05 - i * 0.18, -0.55), V((i % 2 ? 0.3 : -0.3), 0.6, -1), 0.07, 0.4 - i * 0.05);

  /* ---------- the core and the beam ---------- */
  const coreB = joint(chest, C, 'core');
  const cg = G(coreB.abs, C.x, C.y, C.z);
  M(new THREE.OctahedronGeometry(0.13, 0), core, cg).scale.set(1, 1.3, 1);
  glowSprite(cg, '#c56cff', 1.4, V(0, 0, 0.1), 0.65);
  glowSprite(cg, '#f3e0ff', 0.5, V(0, 0, 0.14), 0.9);
  const beamB = joint(chest, C.clone().add(V(0, 0, 0.1)), 'beam');
  {
    // (Built full size only for the gallery's look at the rule.)
    const k = o?.firing ? 1 : PRISM_BEAM_REST;
    const L = 4.5 * k, R = 0.12 * k;
    const bg = G(beamB.abs, C.x, C.y, C.z + 0.1);
    // Aimed down at the floor before it (the sweep is the clip's).
    bg.rotation.x = 0.36;
    const geo = new THREE.CylinderGeometry(R, R * 1.6, L, 8, 1, true);
    geo.translate(0, L / 2, 0);
    geo.rotateX(Math.PI / 2);
    M(geo, beamM, bg);
    const inner = new THREE.CylinderGeometry(R * 0.4, R * 0.6, L, 6, 1, true);
    inner.translate(0, L / 2, 0);
    inner.rotateX(Math.PI / 2);
    M(inner, core, bg);
  }

  /* ---------- head: a small block of stone sunk between the shoulders, a crown of prisms ---------- */
  const headB = joint(chest, V(0, 2.08, 0.1), 'head');
  const head = headB.abs;
  k4.lump(head, rock, 0, 2.18, 0.12, 0.22, 0.17, 0.2, 0.2, 1);
  k4.lump(head, rockL, 0, 2.24, 0.24, 0.2, 0.06, 0.08, 0.2, 0);
  for (const s of [-1, 1]) {
    const e = M(new THREE.BoxGeometry(0.08, 0.02, 0.02), core, head);
    e.position.set(s * 0.075, 2.19, 0.31);
    e.rotation.z = s * -0.25;
    glowSprite(head, '#e3c2ff', 0.22, V(s * 0.075, 2.19, 0.34), 0.9);
  }
  for (let i = 0; i < 5; i++) {
    const a = (i - 2) * 0.42;
    P(head, i === 2 ? crystal : i % 2 ? deep : crystal, V(Math.sin(a) * 0.12, 2.3, 0.1 - Math.abs(a) * 0.05), V(Math.sin(a) * 0.7, 1, -0.2), 0.05, 0.42 - Math.abs(i - 2) * 0.09);
  }

  /* ---------- arms: long six-sided prisms, fists of rock and crystal ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(chest, V(k * 0.72, 1.95, 0), `arm${s}`);
    const sh = armB.abs;
    k4.lump(sh, rockL, k * 0.76, 1.92, 0, 0.2, 0.2, 0.2, 0.3, 0);
    const up = M(hexPrism(0.12, 0.5, 0.02), deep, sh);
    up.position.set(k * 0.78, 1.88, 0.0);
    up.quaternion.setFromUnitVectors(V(0, 1, 0), V(k * 0.22, -1, 0.06).normalize());
    const foreB = joint(sh, V(k * 0.9, 1.36, 0.04), `fore${s}`);
    const fo = foreB.abs;
    k4.lump(fo, rock, k * 0.9, 1.36, 0.04, 0.14, 0.13, 0.14, 0.3, 0);
    const lo = M(hexPrism(0.15, 0.52, 0.02), crystal, fo);
    lo.position.set(k * 0.9, 1.32, 0.05);
    lo.quaternion.setFromUnitVectors(V(0, 1, 0), V(k * 0.08, -1, 0.18).normalize());
    P(fo, crystal, V(k * 1.0, 1.2, -0.02), V(k, 0.5, -0.5), 0.05, 0.3);
    const handB = joint(fo, V(k * 0.95, 0.78, 0.16), `hand${s}`);
    const ha = handB.abs;
    k4.lump(ha, rock, k * 0.95, 0.66, 0.18, 0.21, 0.2, 0.22, 0.3, 1);
    cluster(ha, V(k * 0.95, 0.66, 0.34), V(0, -0.2, 1), 4, 0.18, 0.045);
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
    arms[`hand${s}`] = handB.p;
  }

  // Light pooling round its feet.
  glowSprite(g, '#c56cff', 2.2, V(0, 0.1, 0.1), 0.1);

  return done(c, PRISM_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, core: coreB.p, beam: beamB.p,
    armL: arms.armL!, foreL: arms.foreL!, handL: arms.handL!, armR: arms.armR!, foreR: arms.foreR!, handR: arms.handR!,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
